<template>
  <a-row class="section">
    <div class="section__info">
      <section-strategy-details :data-source="props.dataSource" class="section__details" />
      <template v-if="dataSourcefilters">
        <div class="section__content-filters">
          <a-space :size="20" direction="vertical">
            <a-typography-title class="mrg-bottom-0" :level="5">
              Filtros de contactos asignados:
            </a-typography-title>
            <div v-if="firstSection">
              <a-typography-text strong> {{ firstSection.title }} </a-typography-text>
              <p v-if="itemFirstSection" class="mrg-bottom-0 section__content-text">
                {{ itemFirstSection }}
              </p>
            </div>
            <div v-if="!!itemsSecondSection?.length">
              <a-typography-text strong>
                {{ FILTERS_STRATEGY.secondSection.title }}
              </a-typography-text>
              <p
                v-for="(itemSecondSection, index) in itemsSecondSection"
                :key="index"
                class="mrg-bottom-0 section__content-text"
              >
                <a-typography-text strong class="section__content-label">
                  {{ itemSecondSection.label }}:
                </a-typography-text>
                {{ itemSecondSection.value }}
              </p>
            </div>
            <div v-if="itemsThirdSection">
              <a-typography-text strong>
                {{ FILTERS_STRATEGY.thirdSection.title }}
              </a-typography-text>
              <ul class="section__content-list mrg-bottom-0">
                <li
                  v-for="(itemThirdSection, index) in itemsThirdSection.value"
                  :key="index"
                  class="mrg-bottom-0 section__content-text"
                >
                  {{ itemThirdSection }}
                </li>
              </ul>
            </div>
            <div v-if="!!itemsFourSection?.length">
              <a-typography-text strong>
                {{ FILTERS_STRATEGY.fourSection.title }}
              </a-typography-text>
              <p class="mrg-bottom-0 section__content-text">{{ itemsFourSection }}</p>
            </div>
            <div v-if="!!itemsFiveSection?.length">
              <a-typography-text strong>
                {{ FILTERS_STRATEGY.fiveSection.title }}
              </a-typography-text>
              <p class="mrg-bottom-0 section__content-text">{{ itemsFiveSection }}</p>
            </div>
          </a-space>
        </div>
        <div v-if="showCustomFieldClients" class="section__content-filters">
          <a-space :size="20" direction="vertical">
            <a-typography-title class="mrg-bottom-0" :level="5">
              Filtros personalizados:
            </a-typography-title>
            <div
              v-for="(customField, index) in customFields"
              :key="`custom_client_${index}`"
              class="mrg-bottom-0 section__content-text"
            >
              <a-typography-text strong>{{ customField.name }}: </a-typography-text>
              <template v-if="customField.type === 'text'">
                <p
                  v-for="(item, index) in customField.values"
                  :key="`custom_value_${index}`"
                  class="mrg-bottom-0"
                >
                  {{ item }}
                </p>
              </template>
              <template v-if="customField.type === 'decimal'">
                <span>Desde: </span> {{ customField.values[0] }} <span>Hasta: </span>
                {{ customField.values[1] }}
              </template>
            </div>
          </a-space>
        </div>
      </template>
    </div>

    <div class="section__preview" v-if="dataSource.type_strategy === TypeStrategy.Email">
      <EmailPreview :dataSource="dataSource" />
    </div>
  </a-row>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import SectionStrategyDetails from "@/app/strategies/components/sections/SectionStrategyDetails.vue";
import { DataSourceStrategy, ItemSecondSection } from "@/@types/strategies/modalStrategy";
import { FILTERS_STRATEGY } from "@/app/strategies/utils/modalStrategy";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { TypeStrategy } from "@/@types/strategies/modalStrategy";
import EmailPreview from "@/app/shared/components/organisms/EmailPreview.vue";

const props = defineProps<{
  dataSource: DataSourceStrategy;
}>();

const dataSourcefilters = computed(() => props.dataSource.filters || null);

const firstSection = computed(() => {
  if (!dataSourcefilters.value || !dataSourcefilters.value.clients) return;
  const existFilter = dataSourcefilters.value.clients.some((item) =>
    FILTERS_STRATEGY.firstSection.filters_keys.includes(item.name)
  );
  return existFilter && FILTERS_STRATEGY.firstSection;
});

const itemFirstSection = computed(() => {
  if (!firstSection.value || !dataSourcefilters.value || !dataSourcefilters.value.clients) return;
  const firstSectionFilters = firstSection.value.filters_keys;
  const filter = dataSourcefilters.value.clients?.find((item) =>
    firstSectionFilters.includes(item.name)
  );
  if (!filter) return;
  const filterUsed =
    firstSection.value.filters[filter.name as keyof typeof firstSection.value.filters];

  const value = filterUsed.values[filter.value as keyof typeof filterUsed.values];
  return value;
});

const itemsSecondSection = computed<ItemSecondSection[] | undefined>(() => {
  if (!dataSourcefilters.value || !dataSourcefilters.value.clients) return;
  const items: ItemSecondSection[] = [];
  const secondSection = FILTERS_STRATEGY.secondSection;
  if (!secondSection) return;
  dataSourcefilters.value?.clients.forEach((item) => {
    const secondSectionFilter =
      secondSection.filters[item.name as keyof typeof secondSection.filters];
    if (!secondSectionFilter) return;

    const values = item.values
      ? item.values.map((val) => val[secondSectionFilter.key as keyof typeof val])
      : [];
    items.push({
      label: secondSectionFilter.label,
      value: values.join(", "),
    });
  });
  return items;
});
const itemsThirdSection = computed<ItemSecondSection | undefined>(() => {
  if (!dataSourcefilters.value || !dataSourcefilters.value.clients) return;
  const items: ItemSecondSection[] = [];
  const thirdSection = FILTERS_STRATEGY.thirdSection;
  if (!thirdSection) return;

  dataSourcefilters.value?.clients.forEach((item) => {
    const thirdSectionFilter = thirdSection.filters[item.name as keyof typeof thirdSection.filters];
    if (!thirdSectionFilter) return;

    const values = item.values
      ? item.values.map((val) => val[thirdSectionFilter.key as keyof typeof val])
      : [];
    items.push({
      value: values,
    });
  });
  if (!items.length) return;
  return items[0];
});

const itemsFourSection = computed(() => {
  return itemsForSectionWithDates("fourSection");
});

const itemsFiveSection = computed(() => {
  return itemsForSectionWithDates("fiveSection");
});

const showCustomFieldClients = computed(() => {
  return dataSourcefilters.value && customFields.value && !!customFields.value.length;
});

const customFields = computed(() => {
  if (!dataSourcefilters.value) return;
  const fields = [
    ...dataSourcefilters.value.custom_clients,
    ...dataSourcefilters.value.custom_products,
  ];
  return fields;
});

const itemsForSectionWithDates = (section: string) => {
  const localSection = FILTERS_STRATEGY[section as keyof typeof FILTERS_STRATEGY];
  if (!localSection || !dataSourcefilters.value || !dataSourcefilters.value.clients) return;
  const fourSectionFilters = localSection.filters_keys;
  if (!fourSectionFilters) return;
  const filter = dataSourcefilters.value.clients?.find((item) =>
    fourSectionFilters.includes(item.name)
  );
  if (!filter) return;
  return filter.values
    ? `Desde: ${dateTimeToSlashFormat(filter.values[0], false)} Hasta ${dateTimeToSlashFormat(
        filter.values[1],
        false
      )}`
    : "";
};

// const scaleIframeContent = () => {
//   if (props.dataSource.template_email) {
//     const scale = 0.7;
//     const iframe = document.getElementById("template_email");
//     if (iframe && iframe.contentWindow) {
//       iframe.contentWindow.document.body.style.zoom = scale;
//     }
//   }
// };
</script>

<style lang="sass" scoped>
.section
  &__content-filters
    margin-top: 24px
    background: $base-ninja-100
    border-radius: 4px
    padding: 16px
  &__content-text, &__content-label
    color: $text-secondary
  &__content-list
    padding-left: 24px
  &__details
    margin-right: 40px
    width: 180px
  &__info
    width: 300px
    margin-right: 20px
  &__preview
    width: 224px
.iframe-email
  border-radius: 8px
  border: 1px solid #00000026
</style>
